import React, { useRef, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Icon, ICONS, Select } from '@ohif/ui';

interface AuditFilterProps {
  selectedTimeFrame: any;
  setSelectedTimeFrame: (value: any) => void;
  selectedReportStatus: any;
  setSelectedReportStatus: (value: any) => void;
  onApplyFilter: (values: any) => void;
  onResetFilter: () => void;
  onClearAll: () => void;
}

const AuditFilter: React.FC<AuditFilterProps> = ({
  selectedTimeFrame,
  setSelectedTimeFrame,
  selectedReportStatus,
  setSelectedReportStatus,
  onApplyFilter,
  onResetFilter,
  onClearAll
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const formikRef = useRef(null);
  const [selectedCount, setSelectedCount] = useState(0);

  const dateRangeOptions = [
    { label: 'Today', value: 'current_date' },
    { label: 'Last One Week', value: 'past_1_week' },
    { label: 'Last One Month', value: 'past_1_month' },
    { label: 'Last One Year', value: '1_year' },
  ];

  const reportStatusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Authorized', value: 'authorized' },
  ];

  const handleTimeFrameChange = (selectedOption) => {
    setSelectedTimeFrame(selectedOption);
    updateSelectedCount(selectedOption, selectedReportStatus);
  };

  const handleReportStatusChange = (selectedOption) => {
    setSelectedReportStatus(selectedOption);
    updateSelectedCount(selectedTimeFrame, selectedOption);
  };

  const updateSelectedCount = (timeFrame, reportStatus) => {
    let count = 0;
    if (timeFrame) count += 1;
    if (reportStatus && reportStatus.value !== 'all') count += 1;
    setSelectedCount(count);
  };

  const handleSaveForm = (values) => {
    setIsFilterOpen(false);
    onApplyFilter(values);
  };

  const handleResetForm = () => {
    setSelectedTimeFrame(null);
    setSelectedCount(0);
    if (formikRef.current) {
      formikRef.current.resetForm();
      formikRef.current.setFieldValue('timeFrame', '');
    }
    onResetFilter();
  };

  const filterFormInitialValue = {
    timeFrame: selectedTimeFrame?.value || '',
  };

  // Close filter popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isFilterOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    updateSelectedCount(selectedTimeFrame, selectedReportStatus);
  }, [selectedTimeFrame, selectedReportStatus]);

  return (
    <div style={{ position: 'relative' }}>
      {selectedCount > 0 && (
        <span className="badge absolute top-0 left-0 translate-x-1/2 translate-y-1/2 z-10">
          {selectedCount}
        </span>
      )}
      <button
        ref={buttonRef}
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className="filter-button relative"
      >
        <span className='filterIcon'><img src={ICONS.filterIcon} alt="down arrow" /></span> Filter
      </button>
      <button onClick={onClearAll} className="filter-button">
        <span className='filterIcon'><Icon name="ClearFilters" className="text-black cursor-pointer" width="16" height="16"></Icon></span>
        Clear
      </button>
      {isFilterOpen && (
        <div
          ref={popupRef}
          className="filter-popup"
          style={{
            position: 'absolute',
            top: buttonRef.current.offsetHeight + 10,
            left: 0,
            zIndex: 100,
            backgroundColor: "#ffffff",
            padding: '16px',
            borderRadius: "16px",
            width: "360px",
            boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.16)',
          }}
        >
          <Formik
            key={JSON.stringify(selectedTimeFrame)}
            initialValues={filterFormInitialValue}
            onSubmit={(values, { setSubmitting }) => {
              handleSaveForm(values);
              setSubmitting(false);
            }}
            innerRef={formikRef}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <div className='formRow'>
                  <div className='formCol'>
                    <div className='form__Field'>
<label htmlFor='timeFrame' className="form__label">Select Time Frame</label>
                  <Select
                    id='timeFrame'
                    placeholder='Select'
                    className='custom-bg-white'
                    isClearable={false}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={dateRangeOptions}
                    value={selectedTimeFrame}
                    onChange={handleTimeFrameChange}
                  />
                    </div>
                  </div>
                </div>
                <div className='formRow'>
                  <div className='formCol'>
                    <div className='form__Field'>
                      <label htmlFor='reportStatus' className="form__label">Select Report Status</label>
                  <Select
                    id='reportStatus'
                    placeholder='Select'
                    className='custom-bg-white'
                    isClearable={false}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={reportStatusOptions}
                    value={selectedReportStatus}
                    onChange={handleReportStatusChange}
                  />
                    </div>
                  </div>
                </div>

                <div className='filter-bottom'>
                  <button
                    type="submit"
                    className='custom-button main-theme mr-2'
                  >
                    Apply
                  </button>
                  <button type="button" className='custom-button' onClick={handleResetForm}>Clear</button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default AuditFilter;
